import React, { useCallback } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { StyleSheet } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

import { Divider, Stack } from '@/components/layout';
import { MutateInfoAvatarSection } from '@/components/template/mutate-info/mutate-info-avatar';
import { showConfirm } from '@/components/ui/alert';
import { STRINGS } from '@/constants/strings';
import MutatePlayerView from '@/features/player/components/mutate-player';
import { createStyles, useScaling } from '@/hooks';
import { useBackPress } from '@/hooks/use-back-press';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export type MutateInfoAvatarForm = {
  avatar?: { uri?: string | null; fileSize?: number | null } | null;
};

export type MutateInfoLogics<T extends MutateInfoAvatarForm> = {
  form: UseFormReturn<T>;
  onSave: () => void;
  onCancel?: () => void;
  onDelete?: (() => void) | undefined;
  mode: 'create' | 'edit';
};

export type MutateInfoTemplateProps<T extends MutateInfoAvatarForm> = {
  children: React.ReactNode;
  title: {
    create: string;
    edit: string;
  };
  logics: MutateInfoLogics<T>;
};

export const MutateInfoTemplate = <T extends MutateInfoAvatarForm>({
  children,
  title,
  logics,
}: MutateInfoTemplateProps<T>) => {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const sectionGap = scale(24);
  const { form, onSave, onCancel, onDelete, mode } = logics;
  const navigation = useNavigation();

  const defaultCancel = useCallback(() => {
    showConfirm({
      title: STRINGS.common.defaultCancel.noticeTitle,
      message: STRINGS.common.defaultCancel.cancelMessage,
      confirmText: STRINGS.common.defaultCancel.confirmDiscard,
      cancelText: STRINGS.common.defaultCancel.continueEditing,
    }).then(result => {
      if (result) {
        navigation.goBack();
      }
    });
  }, [navigation]);

  useBackPress(() => {
    (onCancel ?? defaultCancel)();
    return true;
  });

  return (
    <FormProvider {...form}>
      <Stack flex={1} backgroundColor={isTablet ? colors['1'] : 'white'}>
        <MutatePlayerView.Header
          title={mode === 'edit' ? title.edit : title.create}
          onDelete={isTablet ? onDelete : undefined}
        />
        <Divider color={colors['5']} spacing={scale(0)} thickness={StyleSheet.hairlineWidth} />

        <KeyboardAwareScrollView
          style={styles.scroll}
          contentContainerStyle={[styles.formContent, isTablet && styles.formContentTablet]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          bottomOffset={scale(12)}
          extraKeyboardSpace={scale(12)}>
          <Stack
            width="100%"
            maxWidth={isTablet ? scale(676) : undefined}
            gap={sectionGap}
            alignSelf="center">
            <MutateInfoAvatarSection />
            {children}
          </Stack>
        </KeyboardAwareScrollView>

        <MutatePlayerView.Footer
          isTablet={isTablet}
          insetBottom={insets.bottom}
          onSave={onSave}
          onCancel={onCancel ?? defaultCancel}
          onDelete={onDelete}
          saveDisabled={false}
          mode={mode}
        />
      </Stack>
    </FormProvider>
  );
};

const useStyles = createStyles(({ scale, isTablet }) => ({
  scroll: {
    flex: 1,
    backgroundColor: isTablet ? colors['1'] : 'white',
  },
  formContent: {
    paddingHorizontal: scale(18),
    paddingTop: scale(16),
    paddingBottom: scale(20),
  },
  formContentTablet: {
    paddingHorizontal: scale(16),
    alignItems: 'center',
  },
}));
